import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import HebcalService from "./hebcal";
import { localeCode } from "./localeCodes";

dayjs.extend(utc);
dayjs.extend(timezone);

const unavailable = "—";
const validDate = date => date instanceof Date && Number.isFinite(date.getTime());

export function createTravelCalendar(org) {
    const coordinate = (value, limit) => (
        (typeof value === "number" || (typeof value === "string" && value.trim() !== ""))
        && Number.isFinite(Number(value)) && Math.abs(Number(value)) <= limit
    );
    if (!coordinate(org?.lat, 90) || !coordinate(org?.long, 180)) return null;
    const calendar = new HebcalService({ ...org, lat: Number(org.lat), long: Number(org.long) });
    return calendar.getTimezone() ? calendar : null;
}

/** A civil date belongs to the place, independently of the visitor's clock. */
export function dayInTimeZone(now, timeZone) {
    if (!validDate(now) || !timeZone) return null;
    try {
        const parts = new Intl.DateTimeFormat("en", {
            timeZone, year: "numeric", month: "2-digit", day: "2-digit",
        }).formatToParts(now);
        const part = type => parts.find(p => p.type === type).value;
        return `${part("year")}-${part("month")}-${part("day")}`;
    } catch {
        return null;
    }
}

function parseDay(day) {
    if (typeof day !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
    const date = new Date(`${day}T12:00:00Z`);
    return validDate(date) && date.toISOString().slice(0, 10) === day ? date : null;
}

export function shiftTravelDay(day, delta) {
    const date = parseDay(day);
    if (!date || !Number.isInteger(delta)) return null;
    date.setUTCDate(date.getUTCDate() + delta);
    return date.toISOString().slice(0, 10);
}

function format(date, locale, timeZone, options) {
    // Never silently substitute the visitor's time zone for the place's.
    if (!validDate(date) || !timeZone) return unavailable;
    try {
        return new Intl.DateTimeFormat(localeCode(locale), { ...options, timeZone }).format(date);
    } catch {
        return unavailable;
    }
}

export function formatZmanimTime(date, locale, timeZone) {
    if (!validDate(date) || !timeZone) return unavailable;
    try {
        const parts = new Intl.DateTimeFormat(localeCode(locale), {
            hour: "2-digit", minute: "2-digit", hour12: false, timeZone,
        }).formatToParts(date);
        // hour12 also works on older engines that ignore hourCycle. Some
        // locales use 24:xx for midnight; keep the site's 00–23 clock instead.
        return parts.map(part => part.type === "hour" && part.value === "24" ? "00" : part.value).join("");
    } catch {
        return unavailable;
    }
}

export function formatZmanimDate(date, locale, timeZone) {
    return format(date, locale, timeZone, { day: "2-digit", month: "2-digit", year: "numeric" });
}

export function formatTravelDay(day, locale) {
    // This is a calendar label, not an instant to convert into another zone.
    return format(parseDay(day), locale, "UTC", {
        weekday: "long", day: "2-digit", month: "2-digit", year: "numeric",
    });
}

export function travelDayData(calendar, day) {
    const empty = { zmanim: [], hdate: null, restPeriod: null };
    const parsed = parseDay(day);
    const timeZone = calendar?.getTimezone();
    if (!parsed || !timeZone) return empty;

    // The daily engines and HDate read LOCAL calendar fields. Give them the
    // selected civil date at local noon, rather than a parsed UTC midnight.
    const localDate = new Date(0);
    localDate.setFullYear(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate());
    localDate.setHours(12, 0, 0, 0);
    const safe = (read, fallback) => { try { return read(); } catch { return fallback; } };
    return {
        zmanim: safe(() => calendar.getZmanimDay(localDate), []),
        hdate: safe(() => calendar.hdate(localDate), null),
        // Rest periods consume a real instant: the start of that day AT THE
        // PLACE, including its DST rules, not midnight on the visitor's device.
        restPeriod: safe(() => calendar.getNextRestPeriod(
            dayjs.tz(`${day}T00:00:00`, timeZone).toDate()
        ), null),
    };
}
